/* Smart size finder — body measurements + fit profile → recommended variant size.

   The model estimates two separate demands and blends them:

   - GIRTH (chest/waist): the main driver of whether a size fits. Tracks
     BMI — alpha sizes sit ~2.5 BMI points apart with M centered near
     BMI 22.5 — averaged with the merchant size chart's weight limits,
     which anchor the same scale to this brand's actual cut.
   - LENGTH: tracks height against the chart's height limits (each size's
     band, ~8cm wide by default).
   - The blend is asymmetric: a garment must accommodate girth, so extra
     length pulls the size UP strongly (tall + slim → size up so it's long
     enough) while a short body pulls DOWN only slightly (short + broad
     still needs the girth).
   - The piece's cut (runs small / large / oversized) shifts the target a
     fraction of a size, then the result rounds and snaps to the sizes the
     product actually has. */
(function () {
  'use strict';

  /* How strongly the length demand pulls the size away from the girth
     demand, per garment type. "up" applies when the body is long for its
     girth, "down" when it is short for it. */
  var GARMENT_PULL = {
    top: { up: 0.45, down: 0.12 },
    bottom: { up: 0.2, down: 0.1 },
    outerwear: { up: 0.45, down: 0.12 },
    dress: { up: 0.5, down: 0.15 }
  };

  /* Cut of the piece, in fractions of a size */
  var FIT_OFFSETS = {
    runs_small: 0.75,
    true_to_size: 0,
    runs_large: -0.75,
    oversized: -1
  };

  /* Universal alpha-size anchors: BMI at the center of each size. Bands
     widen past XL, matching how real size curves stretch at the top. */
  var LADDER_BMI_CENTERS = [17.5, 20, 22.5, 25, 27.5, 30.5, 34.5];
  var SIZE_SYNONYMS = {
    XS: 0, XSMALL: 0, EXTRASMALL: 0,
    S: 1, SM: 1, SMALL: 1,
    M: 2, MED: 2, MEDIUM: 2,
    L: 3, LG: 3, LARGE: 3,
    XL: 4, XLARGE: 4, EXTRALARGE: 4,
    XXL: 5, '2XL': 5, XXLARGE: 5,
    XXXL: 6, '3XL': 6
  };

  /* Chart limits at or beyond this are open-ended ("999" style sentinels) */
  var SENTINEL = 400;

  var context = {
    fitProfile: 'true_to_size',
    garmentType: 'top',
    availableSizes: null
  };

  function parseRules(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.rules && Array.isArray(raw.rules)) return raw.rules;
    return [];
  }

  function getDefaults(raw) {
    if (!raw || Array.isArray(raw)) return {};
    return raw.defaults || {};
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function ladderIndexFor(value) {
    var key = String(value).toUpperCase().replace(/[\s_/-]/g, '');
    return Object.prototype.hasOwnProperty.call(SIZE_SYNONYMS, key) ? SIZE_SYNONYMS[key] : null;
  }

  function normalizeOptionName(option) {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    if (typeof option === 'object' && option.name) return option.name;
    return String(option);
  }

  function extractSizeOption(product) {
    if (!product || !product.options) return { index: -1, values: [] };

    var sizeIndex = -1;
    product.options.forEach(function (option, index) {
      if (/size/i.test(normalizeOptionName(option))) sizeIndex = index;
    });
    if (sizeIndex === -1) return { index: -1, values: [] };

    var values = [];
    (product.variants || []).forEach(function (variant) {
      var value = variant.options[sizeIndex];
      if (value != null && values.indexOf(value) === -1) values.push(value);
    });
    return { index: sizeIndex, values: values };
  }

  /* Piecewise-linear interpolation over ascending [{x, y}] points, with
     linear extrapolation beyond both ends. */
  function interpPoints(x, points) {
    var n = points.length;
    if (n === 0) return null;
    if (n === 1) return points[0].y;
    var lo;
    var hi;
    if (x <= points[0].x) {
      lo = points[0];
      hi = points[1];
    } else if (x >= points[n - 1].x) {
      lo = points[n - 2];
      hi = points[n - 1];
    } else {
      for (var i = 1; i < n; i += 1) {
        if (x <= points[i].x) {
          lo = points[i - 1];
          hi = points[i];
          break;
        }
      }
    }
    if (hi.x === lo.x) return lo.y;
    return lo.y + ((x - lo.x) * (hi.y - lo.y)) / (hi.x - lo.x);
  }

  /* The center of each size's band, from the chart's per-size max limits.
     Sentinel ("999") limits extend the previous band's typical width. */
  function centersFromMaxes(maxes, fallbackWidth) {
    var n = maxes.length;
    var widths = [];
    for (var i = 1; i < n; i += 1) {
      if (maxes[i] < SENTINEL && maxes[i - 1] < SENTINEL && maxes[i] > maxes[i - 1]) {
        widths.push(maxes[i] - maxes[i - 1]);
      }
    }
    widths.sort(function (a, b) { return a - b; });
    var width = widths.length ? widths[Math.floor(widths.length / 2)] : fallbackWidth;
    if (!(width > 0)) width = fallbackWidth;

    var tops = [];
    var prev = null;
    for (var j = 0; j < n; j += 1) {
      var top = maxes[j] < SENTINEL ? maxes[j] : prev === null ? null : prev + width;
      if (top === null || (prev !== null && top <= prev)) return null; /* unusable chart data */
      tops.push(top);
      prev = top;
    }
    return tops.map(function (t, k) {
      var bottom = k === 0 ? t - width : tops[k - 1];
      return (bottom + t) / 2;
    });
  }

  /* Continuous position on this chart's ladder for a measurement */
  function chartIndex(value, maxes, fallbackWidth) {
    var centers = centersFromMaxes(maxes, fallbackWidth);
    if (!centers) return null;
    return interpPoints(value, centers.map(function (c, i) { return { x: c, y: i }; }));
  }

  /* BMI position on the universal XS–3XL ladder, translated onto this
     chart's own rows (so a chart that starts at M or uses only S/M/L
     still maps correctly). Returns null for non-alpha size names. */
  function bmiChartIndex(bmi, rules) {
    var points = [];
    rules.forEach(function (rule, i) {
      var u = ladderIndexFor(rule.size);
      if (u !== null) points.push({ x: u, y: i });
    });
    if (points.length < 2 || points.length < rules.length / 2) return null;
    var universal = interpPoints(
      bmi,
      LADDER_BMI_CENTERS.map(function (c, i) { return { x: c, y: i }; })
    );
    /* Extreme BMIs extrapolate linearly; keep their influence within one
       size of the chart's ends so the blend can't run away. */
    return clamp(interpPoints(universal, points), -1, rules.length);
  }

  function snapToAvailable(size, availableSizes, rules) {
    if (!availableSizes || !availableSizes.length) return size;
    if (availableSizes.indexOf(size) > -1) return size;

    var order = rules.map(function (rule) { return rule.size; });
    var targetIndex = order.indexOf(size);
    if (targetIndex === -1) return availableSizes[0];

    for (var offset = 1; offset < order.length; offset += 1) {
      var larger = order[targetIndex + offset];
      var smaller = order[targetIndex - offset];
      if (larger && availableSizes.indexOf(larger) > -1) return larger;
      if (smaller && availableSizes.indexOf(smaller) > -1) return smaller;
    }
    return availableSizes[availableSizes.length - 1];
  }

  function recommendSize(heightCm, weightKg, config) {
    var rules = config.rules || [];
    if (!rules.length) {
      return { size: null, reason: 'nomatch' };
    }
    if (!(heightCm >= 120 && heightCm <= 230) || !(weightKg >= 30 && weightKg <= 250)) {
      return { size: null, reason: 'invalid' };
    }

    var garmentType = GARMENT_PULL[config.garmentType] ? config.garmentType : 'top';
    var pull = GARMENT_PULL[garmentType];
    var lastIndex = rules.length - 1;

    /* Length demand from the chart's height limits */
    var lengthIndex = chartIndex(heightCm, rules.map(function (r) { return r.maxHeight; }), 8);

    /* Girth demand: the chart's weight limits anchored to this brand's
       cut, averaged with the height-normalized BMI position */
    var weightIndex = chartIndex(weightKg, rules.map(function (r) { return r.maxWeight; }), 15);
    var bmi = weightKg / Math.pow(heightCm / 100, 2);
    var bmiIndex = bmiChartIndex(bmi, rules);

    var girthIndex;
    if (weightIndex !== null && bmiIndex !== null) girthIndex = 0.65 * weightIndex + 0.35 * bmiIndex;
    else if (weightIndex !== null) girthIndex = weightIndex;
    else if (bmiIndex !== null) girthIndex = bmiIndex;
    else girthIndex = lengthIndex; /* height-only chart */
    if (girthIndex === null && lengthIndex === null) return { size: null, reason: 'nomatch' };
    if (lengthIndex === null) lengthIndex = girthIndex;

    /* Girth leads; length pulls up hard, down gently */
    var index = girthIndex + (lengthIndex > girthIndex ? pull.up : pull.down) * (lengthIndex - girthIndex);

    index += FIT_OFFSETS[config.fitProfile] || 0;
    index = clamp(index, 0, lastIndex);

    var rounded = Math.round(index);
    var size = rules[rounded].size;

    /* Sitting just under a size boundary → tip the next size up */
    var between = null;
    var fraction = index - Math.floor(index);
    if (rounded === Math.floor(index) && fraction >= 0.35 && rounded < lastIndex) {
      between = rules[rounded + 1].size;
    }

    var snapped = snapToAvailable(size, config.availableSizes, rules);
    if (between && config.availableSizes && config.availableSizes.indexOf(between) === -1) between = null;
    if (between === snapped) between = null;

    return {
      size: snapped,
      between: between,
      reason: 'match',
      fitProfile: config.fitProfile,
      adjusted: (FIT_OFFSETS[config.fitProfile] || 0) !== 0
    };
  }

  window.SizeFinder = {
    setContext: function (next) {
      context = Object.assign({}, context, next || {});
    },

    getContext: function () {
      return Object.assign({}, context);
    },

    setContextFromProduct: function (product, overrides) {
      var sizeOption = extractSizeOption(product);
      var base = Object.assign({}, context, overrides || {});
      if (sizeOption.values.length) base.availableSizes = sizeOption.values;
      context = base;
    },

    parseConfig: function (raw) {
      var rules = parseRules(raw);
      var defaults = getDefaults(raw);
      return {
        rules: rules,
        fitProfile: defaults.fitProfile || 'true_to_size',
        garmentType: defaults.garmentType || 'top'
      };
    },

    calculate: function (heightCm, weightKg, rawConfig, overrides) {
      var config = Object.assign({}, this.parseConfig(rawConfig), context, overrides || {});
      return recommendSize(heightCm, weightKg, config);
    }
  };
})();
